"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, CircleX } from "lucide-react";
import { meiliSearchClient } from "../../meiliseach/client";
import { useMeilisearchStore } from "../../stores/filter-store";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { MeiliSearchVehicleType } from "../../types/Vehicle.types";
import {
  PostHostEventsEnum,
  sendPostHogEvent,
} from "../../libs/post-hog/post-hog-send-event";
import { getCookie } from "cookies-next";
import { stringToSlug } from "../../utils/common";

type MeiliSearchResponse = {
  hits: MeiliSearchVehicleType[];
  estimatedTotalHits: number;
  limit: number;
  offset: number;
  processingTimeMs: number;
  query: string;
};

const MeilisearchAutocomplete = () => {
  const { push } = useRouter();
  const { query, setQuery } = useMeilisearchStore();
  const [hits, setHits] = useState<any[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchHits = async () => {
      if (query !== "") {
        const index = meiliSearchClient.index("vehicles");
        const response: MeiliSearchResponse = await index.search(query, {
          filter: "",
          limit: 50,
        });

        setHits(response.hits);
        setSelectedIndex(0);
      } else {
        setHits(null);
      }
    };

    fetchHits();
  }, [query]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!hits || hits.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        if (selectedIndex < hits.length - 1) {
          setSelectedIndex(selectedIndex + 1);
        }
        break;
      case "ArrowUp":
        if (selectedIndex > 0) {
          setSelectedIndex(selectedIndex - 1);
        }
        break;
      case "Escape":
        setHits(null);
        break;
      case "Enter":
        push(
          `/veiculo/${stringToSlug(
            `${hits[selectedIndex].make}-${hits[selectedIndex].model}-${hits[selectedIndex].category ? hits[selectedIndex].category : hits[selectedIndex].style ? hits[selectedIndex].style : ""}-${hits[selectedIndex].transmission ?? hits[selectedIndex].typeStart}-${hits[selectedIndex].fuel}-${hits[selectedIndex].modelYear}-${hits[selectedIndex].color}:${hits[selectedIndex].id}`
          )}`
        );
        break;
      default:
        break;
    }
  };

  const handlePostHogEvent = async (vehicle: MeiliSearchVehicleType) => {
    const email: string =
      ((await getCookie("leadEmail")) as string) ?? undefined;

    sendPostHogEvent({
      eventName: PostHostEventsEnum.USER_SEARCH_HOME_AUTOCOMPLETE,
      email: email,
      eventProperties: { ...vehicle },
    });
  };

  return (
    <div>
      <div className="ais-SearchBox-input w-full bg-white border border-slate-300 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-sky-500 rounded-md focus:ring-1 flex items-center relative !pl-0">
        <Search color="#6b6464" size={17} className="absolute top-3 left-3" />
        <input
          className="border-none w-full pl-[2.5rem] text-sm rounded-[3px] shadow-inner py-2.5"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar veículos"
        />
        {query && (
          <CircleX
            color="#6b6464"
            size={17}
            className="absolute top-3 right-3 hover:shadow-xl cursor-pointer"
            onClick={() => setQuery("")}
          />
        )}
      </div>
      {hits && (
        <div className="relative" ref={resultsRef}>
          <div className="absolute z-50 w-full max-h-96 overflow-y-auto rounded-[3px] bg-white shadow-inner p-1">
            {hits.map((hit, index) => {
              const detailsVehiclePath = stringToSlug(
                `${hit.make}-${hit.model}-${hit.category ? hit.category : hit.style ? hit.style : ""}-${hit.transmission ?? hit.typeStart}-${hit.fuel}-${hit.modelYear}-${hit.color}:${hit.id}`
              );

              return (
                <Link
                  href={`/veiculo/${detailsVehiclePath}`}
                  key={index + hit.version + hit.id}
                  className={`flex items-center border-b border-gray-200 p-3 hover:bg-gray-100 cursor-pointer ${
                    index === selectedIndex ? "bg-gray-200" : ""
                  }`}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => handlePostHogEvent(hit)}
                >
                  <div className="w-20 h-20 mr-4 flex-none md:w-16 md:h-16 relative">
                    {hit.images && (
                      <Image
                        src={hit.images[0]}
                        alt={`${hit.make} ${hit.model}`}
                        className="w-full h-full object-cover rounded-md"
                        fill
                      />
                    )}
                  </div>
                  <div className="flex-grow">
                    <h4 className="text-md md:text-lg font-semibold">
                      {hit.make} {hit.model}
                    </h4>
                    <p className="text-sm md:text-md">{hit.version}</p>
                    <p className="text-sm text-gray-600">
                      {hit.modelYear ?? ""} - {hit.color ?? ""} -{" "}
                      {hit.transmission ?? ""}
                    </p>
                  </div>
                  <div className="flex-none text-right text-lg font-semibold hidden md:block">
                    {(hit.price ?? 0).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeilisearchAutocomplete;
